import * as React from "react";
import { alpha } from "@mui/material/styles";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { svgIconClasses } from "@mui/material/SvgIcon";
import { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";
import { toggleButtonClasses } from "@mui/material/ToggleButton";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { purple } from "../themePrimitives";

export const inputsCustomizations = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: "border-box",
        transition: "all 100ms ease-in",
        "&:focus-visible": {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: "2px",
        },
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true, // 👈 remove elevação padrão do contained
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 999,
        textTransform: "none",
        boxShadow: "none", // 👈 remove sombra no estado normal

        "&:hover": {
          boxShadow: "none", // 👈 garante que não volta
        },

        "&:active": {
          boxShadow: "none",
        },

        "&.Mui-focusVisible": {
          boxShadow: "none",
        },

        variants: [
          {
            props: { size: "small" },
            style: {
              height: "2.25rem",
              padding: "8px 12px",
            },
          },
          {
            props: { size: "medium" },
            style: {
              height: "2.5rem",
            },
          },

          {
            props: { color: "primary", variant: "contained" },
            style: {
              color: "white",
              backgroundColor: purple[600],
              border: "none",
              boxShadow: "none",

              "&:hover": {
                backgroundImage: "none",
                backgroundColor: purple[700],
                boxShadow: "none",
              },

              "&:active": {
                backgroundColor: purple[800],
                boxShadow: "none",
              },

              ...theme.applyStyles("dark", {
                color: "black",
                backgroundColor: purple[50],
                backgroundImage: `linear-gradient(to bottom, ${purple[100]}, ${purple[50]})`,
                border: `1px solid ${purple[50]}`,
                boxShadow: "none",

                "&:hover": {
                  backgroundImage: "none",
                  backgroundColor: purple[300],
                  boxShadow: "none",
                },

                "&:active": {
                  backgroundColor: purple[400],
                  boxShadow: "none",
                },
              }),
            },
          },

          {
            props: { color: "secondary", variant: "contained" },
            style: {
              color: "white",
              backgroundColor: purple[300],
              backgroundImage: `linear-gradient(to bottom, ${alpha(
                purple[400],
                0.8,
              )}, ${purple[600]})`,
              border: `1px solid ${purple[600]}`,
              boxShadow: "none",

              "&:hover": {
                backgroundColor: purple[700],
                boxShadow: "none",
              },

              "&:active": {
                backgroundColor: purple[700],
                backgroundImage: "none",
                boxShadow: "none",
              },
            },
          },

          {
            props: { variant: "outlined" },
            style: {
              color: (theme.vars || theme).palette.text.primary,
              border: "1px solid",
              borderColor: purple[200],
              backgroundColor: alpha(purple[50], 0.3),
              boxShadow: "none",

              "&:hover": {
                backgroundColor: purple[100],
                borderColor: purple[300],
                boxShadow: "none",
              },

              "&:active": {
                backgroundColor: purple[200],
                boxShadow: "none",
              },

              ...theme.applyStyles("dark", {
                backgroundColor: purple[800],
                borderColor: purple[700],
                boxShadow: "none",

                "&:hover": {
                  backgroundColor: purple[900],
                  borderColor: purple[600],
                  boxShadow: "none",
                },

                "&:active": {
                  backgroundColor: purple[900],
                  boxShadow: "none",
                },
              }),
            },
          },

          {
            props: { color: "secondary", variant: "outlined" },
            style: {
              color: purple[700],
              border: "1px solid",
              borderColor: purple[200],
              backgroundColor: purple[50],
              boxShadow: "none",

              "&:hover": {
                backgroundColor: purple[100],
                borderColor: purple[400],
                boxShadow: "none",
              },

              "&:active": {
                backgroundColor: alpha(purple[200], 0.7),
                boxShadow: "none",
              },

              ...theme.applyStyles("dark", {
                color: purple[50],
                border: "1px solid",
                borderColor: purple[900],
                backgroundColor: alpha(purple[900], 0.3),
                boxShadow: "none",

                "&:hover": {
                  borderColor: purple[700],
                  backgroundColor: alpha(purple[900], 0.6),
                  boxShadow: "none",
                },

                "&:active": {
                  backgroundColor: alpha(purple[900], 0.5),
                  boxShadow: "none",
                },
              }),
            },
          },

          {
            props: { variant: "text" },
            style: {
              color: purple[600],
              boxShadow: "none",

              "&:hover": {
                backgroundColor: purple[100],
                boxShadow: "none",
              },

              "&:active": {
                backgroundColor: purple[200],
                boxShadow: "none",
              },

              ...theme.applyStyles("dark", {
                color: purple[50],
                boxShadow: "none",

                "&:hover": {
                  backgroundColor: purple[700],
                  boxShadow: "none",
                },

                "&:active": {
                  backgroundColor: alpha(purple[700], 0.7),
                  boxShadow: "none",
                },
              }),
            },
          },

          {
            props: { color: "secondary", variant: "text" },
            style: {
              color: purple[700],
              boxShadow: "none",

              "&:hover": {
                backgroundColor: alpha(purple[100], 0.5),
                boxShadow: "none",
              },

              "&:active": {
                backgroundColor: alpha(purple[200], 0.7),
                boxShadow: "none",
              },

              ...theme.applyStyles("dark", {
                color: purple[100],
                boxShadow: "none",

                "&:hover": {
                  backgroundColor: alpha(purple[900], 0.5),
                  boxShadow: "none",
                },

                "&:active": {
                  backgroundColor: alpha(purple[900], 0.3),
                  boxShadow: "none",
                },
              }),
            },
          },
        ],
      }),
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",
        borderRadius: (theme.vars || theme).shape.borderRadius,
        textTransform: "none",
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: 0,
        color: (theme.vars || theme).palette.text.primary,
        border: "1px solid ",
        borderColor: purple[200],
        backgroundColor: alpha(purple[50], 0.3),
        "&:hover": {
          backgroundColor: purple[100],
          borderColor: purple[300],
        },
        "&:active": {
          backgroundColor: purple[200],
        },
        ...theme.applyStyles("dark", {
          backgroundColor: purple[800],
          borderColor: purple[700],
          "&:hover": {
            backgroundColor: purple[900],
            borderColor: purple[600],
          },
          "&:active": {
            backgroundColor: purple[900],
          },
        }),
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              width: "2.25rem",
              height: "2.25rem",
              padding: "0.25rem",
              [`& .${svgIconClasses.root}`]: { fontSize: "1rem" },
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              width: "2.5rem",
              height: "2.5rem",
            },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: "10px",
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: purple[600],
        },
        ...theme.applyStyles("dark", {
          [`& .${toggleButtonGroupClasses.selected}`]: {
            color: "#fff",
          },
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: "12px 16px",
        textTransform: "none",
        borderRadius: "10px",
        fontWeight: 500,
        ...theme.applyStyles("dark", {
          color: purple[400],
          [`&.${toggleButtonClasses.selected}`]: {
            color: purple[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon
          sx={{ color: "hsla(210, 0%, 0%, 0.0)" }}
        />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: "1px solid ",
        borderColor: alpha(purple[300], 0.8),
        backgroundColor: alpha(purple[100], 0.4),
        transition: "border-color, background-color, 120ms ease-in",
        "&:hover": {
          borderColor: purple[300],
        },
        "&.Mui-focusVisible": {
          outline: `3px solid ${alpha(purple[600], 0.5)}`,
          outlineOffset: "2px",
          borderColor: purple[400],
        },
        "&.Mui-checked": {
          color: "white",
          backgroundColor: purple[600],
          borderColor: purple[600],
        },
        ...theme.applyStyles("dark", {
          borderColor: alpha(purple[700], 0.8),
          backgroundColor: alpha(purple[900], 0.8),
          "&:hover": {
            borderColor: purple[300],
          },
          "&.Mui-focusVisible": {
            borderColor: purple[400],
            outline: `3px solid ${alpha(purple[600], 0.5)}`,
            outlineOffset: "2px",
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: "none",
      },
      input: {
        "&::placeholder": {
          opacity: 0.7,
          color: purple[600],
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: (theme.vars || theme).shape.borderRadius,
        backgroundColor: (theme.vars || theme).palette.background.default,
        transition: "border-color 120ms ease-in",
      }),

      input: {
        "&::placeholder": {
          opacity: 0.6,
          color: purple[600],
        },
      },
    },
  },

  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles("dark", {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
      }),
    },
  },
};
